/**
 * The transport and edit toolbar.
 *
 * Icons only, as in the sketch, with a text label under each on wide screens
 * turned off — the tooltip and the accessible name carry the meaning, and the
 * row stays quiet enough to look at for an hour. Every button here is also
 * reachable from the keyboard, and every keyboard shortcut is also a button.
 */

import type { JSX } from 'react';
import { useSession } from '../state/store';
import { usePlayback } from '../state/playback';
import {
  DiceIcon,
  MetronomeIcon,
  PauseIcon,
  PlayIcon,
  RedoIcon,
  StopIcon,
  UndoIcon,
} from './Icons';
import { IconButton } from './IconButton';

export function Toolbar({ onNewExercise }: { onNewExercise: () => void }): JSX.Element {
  const status = usePlayback((s) => s.status);
  const toggle = usePlayback((s) => s.toggle);
  const stop = usePlayback((s) => s.stop);
  const tap = usePlayback((s) => s.tap);
  const tapDisplay = usePlayback((s) => s.tapDisplay);

  const undo = useSession((s) => s.undo);
  const redo = useSession((s) => s.redo);
  const historyIndex = useSession((s) => s.historyIndex);
  const historyLength = useSession((s) => s.history.length);

  const playing = status === 'playing';

  return (
    <div className="toolbar" role="toolbar" aria-label="Transport and editing">
      <div className="toolbar__group">
        <IconButton
          label={playing ? 'Pause (Space)' : 'Play (Space)'}
          onClick={toggle}
          icon={playing ? <PauseIcon /> : <PlayIcon />}
        />
        <IconButton
          label="Stop"
          onClick={stop}
          disabled={status === 'stopped'}
          icon={<StopIcon />}
        />
        <div className="tap">
          <IconButton label="Tap tempo — tap along to set the speed" onClick={tap} icon={<MetronomeIcon />} />
          {/* The reading fades once the tapping stops, so the row returns to its
              resting state instead of carrying a permanent number. */}
          <span className={tapDisplay == null ? 'tap__readout' : 'tap__readout tap__readout--visible'} aria-live="polite">
            {tapDisplay ?? ''}
          </span>
        </div>
      </div>

      <div className="toolbar__spacer" />

      <div className="toolbar__group">
        <IconButton
          label="Undo (⌘Z)"
          onClick={undo}
          disabled={historyIndex <= 0}
          icon={<UndoIcon />}
        />
        <IconButton
          label="Redo (⇧⌘Z)"
          onClick={redo}
          disabled={historyIndex >= historyLength - 1}
          icon={<RedoIcon />}
        />
        <IconButton label="New exercise" onClick={onNewExercise} icon={<DiceIcon />} />
      </div>
    </div>
  );
}
